import { PolicyValueType } from 'components/PolicyFilterDropdown';
import { atom, useAtom } from 'jotai';
import { atomWithReset, atomWithStorage, useResetAtom } from 'jotai/utils';
import { companyIdAtom } from 'views/state';
import { DEFAULT_PAGE_SIZE } from 'views/types';

export const DEFAULT_COLUMN_KEYS_ASSET = [
  // 'id',
  'name',
  'alias',
  // 'host_id',
  'os',
  'address',
  'last_compliance_scan',
  'failed_count',
  'passed_count',
  'compliance',
  'actions',
];

export const componentHeightAtom = atom(0);

export const columnKeysAssetAtom = atomWithStorage(
  'asset_listing_active_column_keys',
  DEFAULT_COLUMN_KEYS_ASSET,
);

export const filterValuesAtom = atomWithStorage<Record<string, any>>(
  'asset_listing_filter_values_atom',
  {},
);

const DateFilterKeys = [
  'ca_modified',
  'last_checked_in',
  'last_compliance_scan',
];

export const parsedFilterValuesAtom = atom<Record<string, any>>((get) => {
  const filterValues = get(filterValuesAtom);
  const updatedFilterValues = {
    ...filterValues,
    ...DateFilterKeys.reduce((acc, key) => {
      if (filterValues[key]) {
        acc[key] = filterValues[key].map((dateRange) => ({
          from: dateRange.from ? new Date(dateRange.from) : null,
          to: dateRange.to ? new Date(dateRange.to) : null,
        }));
      }
      return acc;
    }, {}),
  };
  return updatedFilterValues;
});

export const selectedRowIdsAtom = atomWithReset<number[]>([]);

export const forceRerenderAssetGridAtom = atom<string>('');
export const assetDataRefreshAtom = atomWithStorage<string>(
  'asset_refresh',
  '',
);

export const paginationConfigAtom = atomWithStorage(
  'pc_dashboard_assets_pagination_config',
  {
    page_size: DEFAULT_PAGE_SIZE,
    page: 1,
  },
);
export const orderbyAtom = atomWithReset<string>('host_name');
export const sortOrderAtom = atomWithReset<'desc' | 'asc'>('asc');

export const policyIdFilterAtom = atomWithStorage<PolicyValueType>(
  'pc_dashboard_assets_policy_filter',
  'all',
);

export const isQuerystringFiltersInitializedAtom = atom<boolean>(true);

export const assetDataQueryParamsAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const policyId = get(policyIdFilterAtom);
  const policyIdFilter = policyId === 'all' ? undefined : policyId;
  const paginationConfig = get(paginationConfigAtom);
  const orderBy = get(orderbyAtom);
  const order = get(sortOrderAtom);
  const ordering = orderBy
    .split(',')
    .map(
      (field) =>
        `${order === 'desc' && !field.startsWith('-') ? '-' : ''}${field}`,
    )
    .join(',');
  const filterValues = get(parsedFilterValuesAtom);
  const _assetDataRefresh = get(assetDataRefreshAtom);

  if (companyId && companyId !== -1) {
    const params = {
      company_id: companyId.toString(),
      page: paginationConfig.page.toString(),
      page_size: paginationConfig.page_size.toString(),
      ordering: ordering,
    };

    if (policyIdFilter) {
      params['policy_id'] = policyIdFilter.toString();
    }

    Object.entries(filterValues).forEach(([key, value]) => {
      if (value && value.length > 0) {
        if (DateFilterKeys.includes(key)) {
          const fromDate = value[0]?.from;
          const toDate = value[0]?.to;
          if (fromDate instanceof Date) {
            params[`${key}_after`] = fromDate.toISOString();
          } else {
            console.error({
              msg: 'fromDate is not a valid Date object:',
              fromDate,
            });
          }
          if (toDate) params[`${key}_before`] = toDate?.toISOString();
        } else {
          params[key] = value.join(',');
        }
      }
    });

    return params;
  }
  return undefined;
});

// Define the structure for insights data
interface AssetInsights {
  total: number;
  total_failed_postures: number;
  total_passed_postures: number;
  unique_os_count: number;
  unique_policy_count: number;
  urgent: number;
  critical: number;
  serious: number;
  medium: number;
  minimal: number;
}

// Atom to store insights data for assets
export const assetInsightsAtom = atom<AssetInsights>({
  total: 0,
  total_failed_postures: 0,
  total_passed_postures: 0,
  unique_os_count: 0,
  unique_policy_count: 0,
  urgent: 0,
  critical: 0,
  serious: 0,
  medium: 0,
  minimal: 0,
});

// Update assetDataAtom to include insights
type AssetData = {
  results: any[];
  insights: AssetInsights;
  count: number;
  loading: boolean;
};

export const assetDataAtom = atom<AssetData>({
  results: [],
  insights: {
    total: 0,
    total_failed_postures: 0,
    total_passed_postures: 0,
    unique_os_count: 0,
    unique_policy_count: 0,
    urgent: 0,
    critical: 0,
    serious: 0,
    medium: 0,
    minimal: 0,
  },
  count: 0,
  loading: false,
});

// Add a new atom to store vulnerability counts for checked hosts
export const checkedHostsFindingsMapAtom = atom<Record<number, number>>({});

export const findingsCountForCheckedHostsAtom = atom(async (get) => {
  // Fetching checkedHostIds and allHosts using atoms
  const checkedHostIds = get(selectedRowIdsAtom);
  const findingsMap = get(checkedHostsFindingsMapAtom);
  let total = 0;
  if (checkedHostIds?.length > 0) {
    const allHosts = get(assetDataAtom);
    // NOTE: get the checked hosts based on checkedHostIds
    // NOTE: loop through all checked hosts, and calculate the grand total of findings
    // Getting the checked hosts based on checkedHostIds
    const currentPageHosts = allHosts?.results?.filter((host) =>
      checkedHostIds.includes(host.host_id),
    );

    // Calculate total using both stored and current page values
    checkedHostIds.forEach((hostId) => {
      // Use stored values if available, otherwise 0
      const hostFindings = findingsMap[hostId] || 0;
      total += hostFindings || 0;
    });

    currentPageHosts?.forEach((host) => {
      const totalHostFindings = host.passed_count + host.failed_count;
      if (totalHostFindings !== findingsMap[host.host_id]) {
        total = total - findingsMap[host.host_id] + totalHostFindings;
      }
    });
  }
  return total;
});

export function useOnFilterChangeResetRelatedAtoms() {
  const resetSelectedRowIdsAtom = useResetAtom(selectedRowIdsAtom);
  const resetOrderbyAtom = useResetAtom(orderbyAtom);
  // const resetSortOrderAtom = useResetAtom(sortOrderAtom);
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);

  const resetAtoms = () => {
    resetSelectedRowIdsAtom();
    resetOrderbyAtom();
    // resetSortOrderAtom();
    setPaginationConfig({
      ...paginationConfig,
      page: 1,
    });
  };

  return resetAtoms;
}

/**
 * Hook to reset relevant atoms when the policy filter changes
 * @returns a function that resets the selectedRowIdsAtom, orderbyAtom, sortOrderAtom, and paginationConfigAtom
 */
export const useOnPolicyFilterChangeResetRelatedAtoms = () => {
  const resetAtoms = useOnFilterChangeResetRelatedAtoms();
  const resetFilterValuesAtom = useResetAtom(filterValuesAtom);
  return () => {
    resetAtoms();
    resetFilterValuesAtom();
  };
};
